/**
 * Widget Pagination Hook
 * Implementation of IWidgetPagination
 * Handles widget-level pagination with SSR + CSR data accumulation
 * Uses injectable cache manager for flexibility across page types
 * Uses unified pagination coordinator for sequential coordination
 *
 * Uses types from @lastbrand/storefront-proto-registry for strict type checking
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStore } from 'zustand'

import {
  usePageStateStore,
  type WidgetPaginationDataState,
} from '@src/components/pl-base-widget/contexts/page-state-context'
import { usePLPageMeta } from '@src/components/pl-page-context'
import { getCollectionCacheKey } from '@src/pages/[...slug]/components/collection/utils'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { IWidgetDataFetcher } from '../contracts/data-fetching'
import { UseWidgetPaginationReturn } from '../contracts/pagination'
import { PAGINATION_TYPE, usePaginationCoordinator } from '../coordination/use-pagination-coordinator'
import { getDeviceAwareIntersectionOffset } from '../utils/intersection-offset'
import { defaultPaginationCacheAdapter } from '../utils/pagination-cache-adapters'
import { exposePLExperiments } from '../utils/pl-experiments'

import type { PaginationCacheManager } from '../contracts/pagination-cache'
import type { CacheKeyGenerator } from '../contracts/scroll-cache'
import type { Pagination } from '@lastbrand/storefront-proto-registry/api/v1/common'
import type { WidgetFetchResponse } from '@lastbrand/storefront-proto-registry/api/v1/widgetfetch'

export interface UseWidgetPaginationOptions {
  /**
   * Intersection observer root margin offset.
   * If not provided, defaults to a device-aware lookahead (3500px on desktop,
   * 2000px on mobile/tablet) - aligned with the non-PL CLP convention.
   */
  intersectionOffset?: string
  /**
   * Enable sequential pagination coordination
   * When true, only one widget/layout can paginate at a time (uses coordinator queue)
   * When false (default), widgets can paginate in parallel
   * @default false
   */
  enableCoordination?: boolean
  /**
   * Cache key generator function
   * If not provided, uses pageMeta.paginationCacheKeyGenerator or defaults to getCollectionCacheKey
   */
  cacheKeyGenerator?: CacheKeyGenerator
  /**
   * Pagination cache manager
   * If not provided, uses pageMeta.paginationCacheManager or defaults to collection cache adapter
   */
  cacheManager?: PaginationCacheManager
}

function extractPaginationInfo(data: Record<string, unknown>): Pagination | undefined {
  const pagination = data.pagination as Pagination | undefined
  return pagination
}

/**
 * Hook for widget-level pagination
 * Similar pattern to useSearchResults hook
 * Uses unified pagination coordinator for optional sequential coordination
 *
 * @param widgetId - Unique identifier for the widget
 * @param initialSSRData - Initial widget data from SSR (complete widget structure with pagination info)
 * @param dataFetchingContract - Contract for fetching widget data
 * @param options - Optional configuration for pagination behavior
 * @returns Pagination state and controls, including updated widget data with pagination info
 */
export function useWidgetPagination(
  widgetId: string,
  initialSSRData: Record<string, unknown>,
  dataFetchingContract?: IWidgetDataFetcher,
  options?: UseWidgetPaginationOptions,
): UseWidgetPaginationReturn {
  const enableCoordination = options?.enableCoordination ?? false
  const deviceInfo = useDeviceInfo()
  const intersectionOffset = options?.intersectionOffset ?? getDeviceAwareIntersectionOffset(deviceInfo)

  // Use unified pagination coordinator (works for both widgets and layout)
  const coordinator = usePaginationCoordinator()
  const { requestPagination, releasePagination, currentLoadingId } = coordinator

  // Get page meta for cache key generation and cache manager
  const pageMeta = usePLPageMeta()

  // Use provided options, pageMeta config, or defaults (priority: options > pageMeta > defaults)
  const cacheKeyGenerator = options?.cacheKeyGenerator ?? pageMeta.paginationCacheKeyGenerator ?? getCollectionCacheKey
  const cacheManager = options?.cacheManager ?? pageMeta.paginationCacheManager ?? defaultPaginationCacheAdapter

  // Get page state store (for state management)
  const pageStateStore = usePageStateStore()

  // Get widget state actions from page state store
  const saveWidgetState = useStore(pageStateStore, state => state.widgetStateActions.saveWidgetState)
  const getWidgetState = useStore(pageStateStore, state => state.widgetStateActions.getWidgetState)

  // Generate cache key from page meta using provided generator
  const cacheKey = useMemo(() => {
    return cacheKeyGenerator(pageMeta.pathname, pageMeta.searchParams)
  }, [pageMeta.pathname, pageMeta.searchParams, cacheKeyGenerator])

  // Check for cached state on initialization
  const cachedState: WidgetPaginationDataState | null = useMemo(() => {
    if (!cacheKey || !widgetId) return null

    // First check store (for current session - already restored from cache if available)
    const storeState = getWidgetState(widgetId)
    if (storeState) {
      return storeState
    }

    // Then check cache (fallback - in case store wasn't initialized with cache)
    const cacheState = cacheManager.getWidgetPaginationState(cacheKey, widgetId)
    if (cacheState) {
      // Restore to store for current session
      saveWidgetState(widgetId, cacheState)
      return cacheState
    }

    return null
  }, [cacheKey, widgetId, getWidgetState, saveWidgetState, cacheManager])

  const initialPagination = cachedState?.paginationInfo ?? extractPaginationInfo(initialSSRData)

  // Initialize with cached state if available, otherwise use SSR data
  const [widgetData, setWidgetData] = useState<Record<string, unknown>>(
    cachedState ? { ...initialSSRData } : initialSSRData,
  )
  const [paginatedPages, setPaginatedPages] = useState<WidgetFetchResponse[]>(
    cachedState ? cachedState.paginatedPages : [],
  )
  const [paginationInfo, setPaginationInfo] = useState<Pagination | undefined>(initialPagination)
  const [isLoading, setIsLoading] = useState(false)

  // Track if we've already triggered pagination for the current intersection
  // Prevents multiple triggers while element stays in view
  const hasTriggeredForCurrentIntersection = useRef(false)

  // Update state when SSR data changes (e.g., on route change)
  // Reset to SSR data if route changed (cache key would be different)
  const prevInitialSSRDataRef = useRef(initialSSRData)
  const prevCacheKeyRef = useRef(cacheKey)
  useEffect(() => {
    const prevPagination = extractPaginationInfo(prevInitialSSRDataRef.current)
    const newPagination = extractPaginationInfo(initialSSRData)

    const paginationChanged = prevPagination !== newPagination

    // Check if widget data structure changed (reference comparison)
    const dataChanged = prevInitialSSRDataRef.current !== initialSSRData

    // Check if cache key changed (route changed)
    const cacheKeyChanged = prevCacheKeyRef.current !== cacheKey

    if (dataChanged || paginationChanged || cacheKeyChanged) {
      // If cache key changed, check for new cached state
      if (cacheKeyChanged && cacheKey && widgetId) {
        const newCachedState = cacheManager.getWidgetPaginationState(cacheKey, widgetId)
        if (newCachedState) {
          setWidgetData({ ...initialSSRData })
          setPaginatedPages(newCachedState.paginatedPages)
          setPaginationInfo(newCachedState.paginationInfo)
          saveWidgetState(widgetId, newCachedState)
        } else {
          setWidgetData(initialSSRData)
          setPaginatedPages([])
          setPaginationInfo(newPagination)
        }
      } else {
        // Props changed but cache key is the same - check if we have paginated data
        // If so, don't reset (we might have paginated data that should be preserved)
        const storeState = widgetId ? getWidgetState(widgetId) : undefined
        if (storeState && storeState.paginatedPages.length > 0) {
          // Keep using paginated pages from store
          setWidgetData({ ...initialSSRData })
          setPaginatedPages(storeState.paginatedPages)
          setPaginationInfo(storeState.paginationInfo)
        } else {
          setWidgetData(initialSSRData)
          setPaginatedPages([])
          setPaginationInfo(newPagination)
        }
      }
      prevInitialSSRDataRef.current = initialSSRData
      prevCacheKeyRef.current = cacheKey
    }
  }, [initialSSRData, cacheKey, widgetId, saveWidgetState, cacheManager, getWidgetState])

  // Pagination is controlled by PL response: hasMore must be explicitly true
  const hasMore = paginationInfo?.hasMore === true

  // Disable intersection observer when hasMore is false (PL response says no more data)
  const [intersectRef, isIntersecting] = useIntersect(null, intersectionOffset, 0, false, !hasMore)

  // Reset intersection state when hasMore becomes false to prevent stale triggers
  // useIntersect might keep isIntersecting=true even after disconnecting
  const effectiveIsIntersecting = hasMore ? isIntersecting : false

  useEffect(() => {
    if (!isIntersecting) {
      hasTriggeredForCurrentIntersection.current = false
    }
  }, [isIntersecting])

  // Also reset the trigger latch when a load completes. Without this, if the user
  // stops scrolling while a fetch is in flight (so isIntersecting never flips back
  // to false), the latch stays true forever and the next page is never requested
  // even though the sentinel is still inside the rootMargin band. This is the
  // "scroll fast to footer → pagination stalls until you scroll up and back down"
  // bug. The duplicate-fetch guard inside loadMoreAsync already prevents races.
  useEffect(() => {
    if (!isLoading) {
      hasTriggeredForCurrentIntersection.current = false
    }
  }, [isLoading])

  const loadMoreAsync = useCallback(async () => {
    if (!dataFetchingContract || isLoading || !hasMore) {
      return
    }

    // When coordination is enabled, request permission and wait if queued
    if (enableCoordination) {
      const granted = requestPagination(widgetId, PAGINATION_TYPE.WIDGET)
      if (!granted) {
        // Permission not granted (queued), wait for coordinator to grant permission
        return
      }
    }

    setIsLoading(true)
    try {
      const result = await dataFetchingContract.fetchWidgetData(widgetId, paginationInfo)

      // Register any PL-evaluated experiment assignments returned with this widget page
      // so they flow into active_experiment on subsequent Mixpanel events.
      exposePLExperiments(result.experiments)

      // Store new page data — the full WidgetFetchResponse from proto
      const newPages = [...paginatedPages, result]
      setPaginatedPages(newPages)

      // Pass-through: use response pagination as-is, fallback to stopping pagination
      const updatedPaginationInfo: Pagination | undefined = result.pagination ?? {
        ...paginationInfo,
        hasMore: false,
      }

      setPaginationInfo(updatedPaginationInfo)

      // Save state to store after pagination
      if (cacheKey && widgetId) {
        const widgetState: WidgetPaginationDataState = {
          widgetId,
          dataItems: [], // Not used in new structure, kept for compatibility
          paginationInfo: updatedPaginationInfo,
          paginatedPages: newPages,
        }
        saveWidgetState(widgetId, widgetState)
      }
    } catch (error) {
      console.error('Failed to load more widget data:', error)
      captureException(error instanceof Error ? error : new Error(String(error)), {
        module: MODULES.PL,
        feature: FEATURES.WIDGET_PAGINATION,
        additionalTags: {
          trigger: 'widget_infinite_scroll',
          pageType: pageMeta.pageType ?? 'unknown',
          widgetId,
          pathname: pageMeta.pathname,
        },
        extraInfo: {
          source: 'useWidgetPagination',
          slug: pageMeta.seoData?.slug ?? pageMeta.pathname,
          searchParams: pageMeta.searchParams,
          paginationHasMore: paginationInfo.hasMore,
          paginationCursor: paginationInfo.cursor ?? '',
        },
      })
      setPaginationInfo(prev =>
        prev
          ? {
              ...prev,
              hasMore: false,
            }
          : undefined,
      )
    } finally {
      setIsLoading(false)
      // Only release pagination lock when coordination is enabled
      if (enableCoordination) {
        releasePagination(widgetId)
      }
    }
  }, [
    dataFetchingContract,
    widgetId,
    isLoading,
    hasMore,
    enableCoordination,
    requestPagination,
    releasePagination,
    cacheKey,
    saveWidgetState,
    paginatedPages,
    paginationInfo,
    pageMeta,
  ])

  const loadMore = useCallback(() => {
    void loadMoreAsync()
  }, [loadMoreAsync])

  // Trigger loadMore when intersection detected
  // Behavior differs based on coordination mode:
  // - Parallel (default): Trigger directly when intersecting
  // - Sequential (coordinated): Wait for coordinator permission
  useEffect(() => {
    if (!hasMore) {
      return
    }

    // Sequential mode: Check for coordinator permission
    // Parallel mode: Trigger directly when intersecting
    const hasPermission = enableCoordination ? currentLoadingId === widgetId : true
    if (
      effectiveIsIntersecting &&
      !isLoading &&
      dataFetchingContract &&
      hasPermission &&
      !hasTriggeredForCurrentIntersection.current
    ) {
      hasTriggeredForCurrentIntersection.current = true
      loadMore()
    }
  }, [
    effectiveIsIntersecting,
    isLoading,
    hasMore,
    dataFetchingContract,
    loadMore,
    enableCoordination,
    currentLoadingId,
    widgetId,
  ])

  // Request pagination when intersection detected (only in sequential mode)
  // In parallel mode, widgets can paginate simultaneously without requesting permission
  useEffect(() => {
    if (!hasMore || !enableCoordination) {
      return
    }
    if (effectiveIsIntersecting && !isLoading && dataFetchingContract && currentLoadingId !== widgetId) {
      requestPagination(widgetId, PAGINATION_TYPE.WIDGET)
    }
  }, [
    effectiveIsIntersecting,
    isLoading,
    hasMore,
    enableCoordination,
    dataFetchingContract,
    currentLoadingId,
    widgetId,
    requestPagination,
  ])

  const updatedData: Record<string, unknown> = useMemo(
    () => ({
      ...widgetData,
      pagination: paginationInfo,
      paginatedPages,
    }),
    [widgetData, paginationInfo, paginatedPages],
  )

  // Save state to page-scoped store whenever pagination state changes
  useEffect(() => {
    if (cacheKey && widgetId) {
      const widgetState: WidgetPaginationDataState = {
        widgetId,
        dataItems: [], // Not used in new structure, kept for compatibility
        paginationInfo,
        paginatedPages,
      }
      saveWidgetState(widgetId, widgetState)
    }
  }, [cacheKey, widgetId, paginationInfo, paginatedPages, saveWidgetState])

  return {
    data: updatedData,
    isLoading,
    hasMore,
    loadMore,
    intersectRef,
  }
}
